import React from 'react'
import ReactDOM from 'react-dom/client'
import { createBrowserRouter, RouterProvider, Navigate } from 'react-router-dom'
import { AuthProvider, useAuth } from './contexts/AuthContext'
import { BrandingProvider } from './contexts/BrandingContext'
import { ToastProvider } from './contexts/ToastContext'
import { ErrorBoundary } from './components/ErrorBoundary'
import Dashboard from './pages/Dashboard'
import Login from './pages/Login'
import ProjectWrapper from './pages/ProjectWrapper';
import Sites from './pages/Sites';
import EssentialDocs from './pages/EssentialDocs';
import ProtocolIntelligence from './pages/project/ProtocolIntelligence';
import StudyDesign from './pages/project/StudyDesign';
import DataManagementHub from './pages/project/DataManagementHub';
import ValidationTab from './pages/project/ValidationTab';
import Documents from './pages/project/Documents';
import EDCBuild from './pages/project/EDCBuild';
import CRFBuilder from './pages/project/CRFBuilder';
import Team from './pages/project/Team';
import Activity from './pages/project/Activity';
import Help from './pages/project/Help';
import AcceptInvite from './pages/AcceptInvite';
import AcceptOrgInvite from './pages/AcceptOrgInvite';
import AdminOrganizations from './pages/admin/AdminOrganizations';
import DbSpike from './pages/admin/DbSpike';
import AdminHome from './pages/admin/AdminHome';
import ResetPassword from './pages/ResetPassword';
import ChangePassword from './pages/ChangePassword';
import SetupMfa from './pages/SetupMfa';
import InstructionLibrary from './pages/InstructionLibrary';
import AdminArchive from './pages/AdminArchive';
import AdminUsers from './pages/AdminUsers';
import AdminLayout from './pages/admin/AdminLayout';
import ChainDashboard from './pages/admin/ChainDashboard';
import AdminSso from './pages/admin/AdminSso';
import AdminBranding from './pages/admin/AdminBranding';
import AdminRoute from './components/AdminRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import ApplicationReceived from './pages/ApplicationReceived';
import { resolveAccountAccess } from './features/auth/accountAccess';
import { installClientErrorReporting } from './features/errors/clientErrorReporting';
import './index.css'

// P1 capture: window.onerror and unhandledrejection. Installed before the tree
// mounts so a failure during the first render is still reported. Neither
// listener calls preventDefault, so the browser console and any other handler
// behave exactly as they did before.
installClientErrorReporting();

// Protected Route wrapper
//
// Two questions now, not one: is anyone signed in, and is that account allowed
// to use the platform. The second is the approval gate's frontend half. The
// decision itself lives in `features/auth/accountAccess` as a pure function so
// it can be tested without a React test runner, which web does not have.
//
// The application screen is rendered IN PLACE rather than redirected to. That
// is deliberate on both counts: there is no address to bookmark or bypass, and
// every protected route shows it instead of its own page — including a deep
// link straight into a project. A redirect would also have to render something
// first, which is the dashboard flash this is meant to prevent.
//
// Not a security boundary. Every callable re-decides server-side.
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { user, loading, isAdmin, accountStatus, mustChangePassword, mfaRequired, mfaEnrolled } = useAuth();

  const decision = resolveAccountAccess({
    loading,
    signedIn: !!user,
    isAdmin,
    status: accountStatus,
  });

  // Must come first: `loading` covers the session, the admin claim and the
  // approval status, so nothing renders until all three are known.
  if (decision === 'wait') {
    return <div className="flex items-center justify-center h-screen">Loading...</div>;
  }

  if (decision === 'login') {
    return <Navigate to="/login" />;
  }

  if (decision === 'application') {
    return <ApplicationReceived />;
  }


  if (mustChangePassword) {
    return <Navigate to="/change-password" replace />;
  }

  if (mfaRequired && !mfaEnrolled) {
    return <Navigate to="/setup-mfa" replace />;
  }

  return <>{children}</>;
};

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoute>
        <Dashboard />
      </ProtectedRoute>
    ),
  },
  // New workflow-based project routes
  {
    path: "/project/:projectId",
    element: (
      <ProtectedRoute>
        <ProjectWrapper />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="protocol" replace />
      },
      {
        path: "protocol",
        element: <ProtocolIntelligence />
      },
      {
        path: "study-design",
        element: <StudyDesign />
      },
      {
        path: "data-management",
        element: <DataManagementHub />
      },
      {
        path: "validation",
        element: <ValidationTab />
      },
      {
        path: "documents",
        element: <Documents />
      },
      {
        path: "database-schema",
        element: <EDCBuild />
      },
      {
        path: "crf-builder",
        element: <CRFBuilder />
      },
      {
        path: "consent-builder",
        element: <Navigate to="../study-design" replace />
      },
      {
        path: "team",
        element: <Team />
      },
      {
        path: "activity",
        element: <Activity />
      },
      {
        path: "help",
        element: <Help />
      }
    ]
  },
  {
    path: "/project/:projectId/sites",
    element: (
      <ProtectedRoute>
        <Sites />
      </ProtectedRoute>
    ),
  },
  {
    path: "/project/:projectId/tmf",
    element: (
      <ProtectedRoute>
        <EssentialDocs />
      </ProtectedRoute>
    ),
  },
  // ── Administration area ────────────────────────────────────────────────
  // Every section sits behind AdminRoute: a non-administrator is redirected to
  // the application home and nothing administrative renders while the check is
  // resolving. The server re-authorizes each operation independently.
  // Lives at /platform (platform-operator area, deliberately unadvertised);
  // the URL is obscurity only — AdminRoute + isGlobalAdmin are the gate.
  {
    path: "/platform",
    element: (
      <AdminRoute>
        <AdminLayout />
      </AdminRoute>
    ),
    children: [
      { index: true, element: <Navigate to="users" replace /> },
      { path: "users", element: <AdminUsers /> },
      { path: "sso", element: <AdminSso /> },
      { path: "branding", element: <SuperAdminRoute><AdminBranding /></SuperAdminRoute> },
      { path: "instruction-library", element: <InstructionLibrary /> },
      { path: "archive", element: <AdminArchive /> },
      // SparkVerify chain verification (spec §8). Internal, admin-only.
      { path: "chain", element: <ChainDashboard /> },
      { path: "organizations", element: <AdminOrganizations /> },
      { path: "db-spike", element: <DbSpike /> },
      // Unknown section (e.g. a bookmark to the removed /admin/test-data)
      // lands on a real one rather than rendering an empty shell.
      { path: "*", element: <Navigate to="users" replace /> },
    ],
  },

  // Legacy addresses. These were reachable — and two were linked from the
  // dashboard — before the gate existed, so they are kept as redirects rather
  // than removed. The redirect target is itself gated, so an old bookmark held
  // by a non-administrator lands on the gate, not on the page.
  {
    path: "/instruction-library",
    element: <Navigate to="/platform/instruction-library" replace />,
  },
  {
    path: "/admin/*",
    element: <Navigate to="/platform" replace />,
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "/invite/:token",
    element: <AcceptInvite />,
  },
  {
    path: "/org-invite/:token",
    element: <AcceptOrgInvite />,
  },
  {
    path: "/reset-password",
    element: <ResetPassword />,
  },
  {
    path: "/change-password",
    element: <ChangePassword />,
  },
  {
    path: "/setup-mfa",
    element: <SetupMfa />,
  },
]);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ErrorBoundary>
      <AuthProvider>
        <BrandingProvider>
          <ToastProvider>
            <RouterProvider router={router} />
          </ToastProvider>
        </BrandingProvider>
      </AuthProvider>
    </ErrorBoundary>
  </React.StrictMode>,
)
